/**
 * Mobile Dashboard - Complete mobile experience for Texas Highway Rangers
 * Full-featured dashboard with bottom navigation
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import NavigationIcon from '@mui/icons-material/Navigation';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// Leaflet imports for map view
import { MapContainer, Marker, Popup, TileLayer, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { AuthContext } from '../context/AuthContext';
import { useMobile } from '../context/MobileContext';
import { useAnnouncement } from '../context/AnnouncementContext';
import { ColorModeContext } from '../theme/ThemeProvider';
import api from '../api/client';
import { INCIDENT_TYPES, SERVICE_OPTIONS } from '../constants/incidentOptions';
import { DEFAULT_STATUS_CHECK_MINUTES } from '../constants/systemConfig';
import { getTenCodeLabel } from '../constants/preferences';
import { formatTimeAgo, formatUtcToLocal, formatUtcToLocalTime } from '../utils/dateUtils';
import { getStatusCheckThreshold as getStatusCheckThresholdUtil } from '../utils/statusCheckUtils';
import { useSoundPack } from '../hooks/useSoundPack';
import { resolveDivisionFromCounty, TRAVEL_DIVISION } from '../constants/regions';
import { getEmergencySoundPath, SOUND_EVENTS } from '../constants/audio';

// ============== CONSTANTS ==============
const WAZE_CITY_OPTIONS = [
  "All Cities",
  "McKinney",
  "Allen",
  "Anna",
  "Melissa",
  "Van Alstyne",
  "Richardson",
  "Plano",
  "Sherman",
  "Dallas",
];

const WAZE_TYPE_OPTIONS = [
  { value: "ASSIST_MOTORIST", label: "Assist Motorist" },
  { value: "DEBRIS", label: "Debris" },
  { value: "ACCIDENT", label: "Accident" },
  { value: "ASSIST_OTHER_AGENCY", label: "Assist Other Agency" },
];

const WAZE_DISTANCE_OPTIONS = [
  { value: 0, label: "Show All" },
  { value: 0.5, label: "0.5 miles" },
  { value: 1, label: "1 mile" },
  { value: 1.5, label: "1.5 miles" },
  { value: 2, label: "2 miles" },
  { value: 3, label: "3 miles" },
  { value: 5, label: "5 miles" },
  { value: 10, label: "10 miles" },
];

const MOBILE_INCIDENT_TYPE_EXCLUSIONS = new Set([
  'test',
  'assist ranger',
  'assist other agency',
]);

const shouldShowMobileIncidentType = (type) => {
  const normalizedType = String(type || '').trim().toLowerCase();
  return !MOBILE_INCIDENT_TYPE_EXCLUSIONS.has(normalizedType);
};

// Helper to get Waze alert category from type/subtype
const getWazeAlertCategory = (type, subtype) => {
  const subtypeUpper = (subtype || "").toUpperCase();
  const typeUpper = (type || "").toUpperCase();
  
  if (subtypeUpper.includes("EMERGENCY_VEHICLE")) return "ASSIST_OTHER_AGENCY";
  if (subtypeUpper.includes("CAR_STOPPED")) return "ASSIST_MOTORIST";
  if (typeUpper === "HAZARD") return "DEBRIS";
  if (typeUpper === "ACCIDENT") return "ACCIDENT";
  return "DEBRIS"; // Default
};

// Calculate distance between two GPS coordinates in miles
const calculateDistanceMiles = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const BUSY_REASONS = [
  { value: 'on-scene', label: 'ON SCENE' },
  { value: 'out-of-vehicle', label: 'OUT OF VEHICLE' },
  { value: 'work', label: 'WORK' },
  { value: 'special-event', label: 'SPECIAL EVENT' },
  { value: 'gym-pt', label: 'GYM/PT' },
  { value: 'training', label: 'TRAINING' },
  { value: 'vehicle-maintenance', label: 'VEHICLE MAINTENANCE' },
];

const EXPENSE_CATEGORIES = [
  { id: 1, name: "Roadside Assistance & Motorist Care", description: "Water, blankets, food for stranded motorists." },
  { id: 2, name: "Safety & Visibility Equipment", description: "Cones, flares, LED beacons." },
  { id: 3, name: "Communications & Technology", description: "Radios, antennas, dash cameras." },
  { id: 4, name: "Tools & Operational Equipment", description: "Jump packs, air compressors, hand tools." },
  { id: 5, name: "Fuel & Fluids", description: "Fuel, oil, DEF, coolant." },
  { id: 6, name: "Vehicle Maintenance & Repairs", description: "Maintenance, tires, brakes, parts." },
  { id: 7, name: "Uniforms & Identification", description: "Shirts, vests, badges, patches." },
  { id: 8, name: "Training & Certifications", description: "Safety training, first aid courses." },
  { id: 9, name: "Food & Sustenance", description: "Meals during extended operations." },
  { id: 10, name: "Administrative & Operations", description: "Printing, forms, web services." },
  { id: 11, name: "Other / Miscellaneous", description: "One-off expenses. Requires notes.", requiresNotes: true },
];

const EMERGENCY_CONFIRM_SECONDS = 5;

const MAX_INCOMPLETE = 5;
const STATUS_CHECK_POLL_MS = 30000;
const STATUS_ALERT_REPEAT_MS = 5000;

// ============== MAP CONFIGURATION ==============
const DEFAULT_CENTER = [33.1972, -96.6150]; // DFW area
const DEFAULT_ZOOM = 13;
const FOLLOW_ZOOM = 15;

const LIGHT_TILES = {
  url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
};

const DARK_TILES = {
  url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
};

// Waze alert category colors
const WAZE_CATEGORY_COLORS = {
  ASSIST_MOTORIST: "#166534",
  DEBRIS: "#c2410c",
  ACCIDENT: "#b91c1c",
  ASSIST_OTHER_AGENCY: "#1e40af",
};

// Create Waze marker icon for mobile map
const createMobileWazeIcon = (category, isDarkMode, label) => {
  const color = WAZE_CATEGORY_COLORS[category] || "#6b7280";
  const borderColor = isDarkMode ? "rgba(17, 17, 17, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const textColor = isDarkMode ? "#f9fafb" : "#111827";
  const textBg = isDarkMode ? "rgba(17, 24, 39, 0.85)" : "rgba(255, 255, 255, 0.9)";
  
  const html = `
    <div style="display:flex;flex-direction:row;align-items:center;white-space:nowrap;">
      <div style="
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${color};
        border: 2.5px solid ${borderColor};
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        flex-shrink: 0;
      "></div>
      ${label ? `<div style="
        margin-left: 4px;
        font-size: 11px;
        font-weight: 700;
        color: ${textColor};
        background: ${textBg};
        padding: 3px 6px;
        border-radius: 4px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        box-shadow: 0 1px 3px rgba(0,0,0,0.25);
      ">${label}</div>` : ''}
    </div>
  `;
  
  const labelWidth = label ? Math.max(90, label.length * 8 + 35) : 22;
  
  return L.divIcon({
    className: "mobile-waze-marker",
    html,
    iconSize: [labelWidth, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
  });
};

// Component to follow user location on map
function FollowUserLocation({ position, shouldFollow }) {
  const map = useMap();
  
  useEffect(() => {
    if (position && shouldFollow) {
      map.setView(position, Math.max(FOLLOW_ZOOM, map.getZoom()), { animate: true });
    }
  }, [map, position, shouldFollow]);
  
  return null;
}

// ============== HELPER FUNCTIONS ==============
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'available': return 'success';
    case 'busy': return 'warning';
    case 'emergency': return 'error';
    case 'out-of-service': return 'default';
    default: return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'available': return <CheckCircleIcon />;
    case 'busy': return <RadioButtonCheckedIcon />;
    case 'emergency': return <WarningAmberIcon />;
    default: return <RadioButtonCheckedIcon />;
  }
};

const getWazeTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'accident': return <CarCrashIcon />;
    case 'hazard': return <WarningAmberIcon />;
    case 'road_closed': return <CloseIcon />;
    default: return <HelpOutlineIcon />;
  }
};

// ============== MAIN COMPONENT ==============
export default function MobileDashboard() {
  const { user, logout } = useContext(AuthContext);
  const { forceDesktopView, setForceDesktopView } = useMobile();
  const { mode, toggleColorMode } = useContext(ColorModeContext);
  const { snowbankEnabled } = useAnnouncement();
  const { playCue } = useSoundPack();
  const useTenCodes = user?.useTenCodes || false;
  const normalizedRole = (user?.role ?? '').toLowerCase();
  const isIncidentRestricted = !!user?.incidentCreationRestricted && normalizedRole !== 'admin' && normalizedRole !== 'supervisor';

  // Navigation state
  const [navValue, setNavValue] = useState(0);

  // Status state
  const [status, setStatus] = useState('available');
  const preEmergencyStatusRef = useRef('available');
  const [busyReason, setBusyReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusInitialized, setStatusInitialized] = useState(false);
  const [statusNotice, setStatusNotice] = useState(null);

  // Emergency self-cancel state
  const [selfCancelOpen, setSelfCancelOpen] = useState(false);
  const [selfCancelReason, setSelfCancelReason] = useState('');
  const [selfCancelPin, setSelfCancelPin] = useState('');
  const [selfCancelLoading, setSelfCancelLoading] = useState(false);
  const [selfCancelError, setSelfCancelError] = useState('');
  const [emergencyConfirmOpen, setEmergencyConfirmOpen] = useState(false);
  const [emergencyCountdown, setEmergencyCountdown] = useState(EMERGENCY_CONFIRM_SECONDS);
  const [emergencyActivating, setEmergencyActivating] = useState(false);
  const emergencyAudioRef = useRef(null);
  const emergencyCountdownIntervalRef = useRef(null);

  // GPS state
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [gpsDivision, setGpsDivision] = useState(null); // GPS-resolved division from county

  // New incident modal state
  const [newIncidentOpen, setNewIncidentOpen] = useState(false);
  const [incidentCreateError, setIncidentCreateError] = useState('');

  // Active units state
  const [activeUnits, setActiveUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Waze alerts state
  const [wazeAlerts, setWazeAlerts] = useState([]);
  const [wazeLoading, setWazeLoading] = useState(false);
  const [wazeCityFilter, setWazeCityFilter] = useState(() => {
    if (typeof window === "undefined") return [];
    const stored = window.localStorage.getItem("mobileWazeCityFilter");
    return stored ? JSON.parse(stored) : [];
  });
  const [wazeTypeFilter, setWazeTypeFilter] = useState(() => {
    if (typeof window === "undefined") return [];
    const stored = window.localStorage.getItem("mobileWazeTypeFilter");
    return stored ? JSON.parse(stored) : [];
  });
  const [wazeDistanceFilter, setWazeDistanceFilter] = useState(() => {
    if (typeof window === "undefined") return 5;
    const stored = window.localStorage.getItem("mobileWazeDistanceFilter");
    return stored !== null ? JSON.parse(stored) : 5;
  });
  const [claimingWazeId, setClaimingWazeId] = useState(null);
  const [wazeViewMode, setWazeViewMode] = useState('map'); // 'list' or 'map' - default to map for mobile
  const [wazeConfigOpen, setWazeConfigOpen] = useState(false);
  const [followMeEnabled, setFollowMeEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("mobileFollowMe");
    return stored !== null ? JSON.parse(stored) : true;
  });

  // Incidents state
  const [incidents, setIncidents] = useState([]);
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [myIncidents, setMyIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Status check state
  const [statusCheckIncidents, setStatusCheckIncidents] = useState([]);
  const [statusCheckRangers, setStatusCheckRangers] = useState([]);
  const [statusAcknowledgements, setStatusAcknowledgements] = useState({});
  const [statusModalIncident, setStatusModalIncident] = useState(null);
  const [statusBackdropAlert, setStatusBackdropAlert] = useState(false);
  const [statusCheckSettings, setStatusCheckSettings] = useState({
    defaultMinutes: DEFAULT_STATUS_CHECK_MINUTES,
    overrides: {},
  });
  const statusModalOpen = Boolean(statusModalIncident);
  const statusBackdropTimerRef = useRef(null);
  const statusAudioTimerRef = useRef(null);
  const statusAlertAudioRef = useRef(null);
  const statusModalPinnedRef = useRef(null);
  const statusModalHoldUntilRef = useRef(0);

  // Edit incident dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editIncident, setEditIncident] = useState(null);
  const [editForm, setEditForm] = useState({
    services: [],
    notes: '',
    vehicle: { year: '', make: '', model: '', color: '', state: '', plate: '' },
  });
  const [editErrors, setEditErrors] = useState([]);

  // Warning dialog
  const [incompleteWarningOpen, setIncompleteWarningOpen] = useState(false);

  // Expense form state
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    purchaseDate: new Date().toISOString().split('T')[0],
    category: '',
    itemDescription: '',
    amountDollars: '',
    notes: '',
  });
  const [expenseReceipt, setExpenseReceipt] = useState(null);
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);
  const [expenseSuccess, setExpenseSuccess] = useState(false);
  const [expenseError, setExpenseError] = useState(null);

  // ============== GPS TRACKING ==============
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError(true);
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setGps({ 
          lat: p.coords.latitude, 
          lng: p.coords.longitude, 
          accuracy: p.coords.accuracy,
          heading: p.coords.heading, // Capture heading for direction detection
          speed: p.coords.speed // Capture speed for breadcrumbs
        });
        setGpsError(false);
      },
      (err) => {
        console.error('GPS error:', err);
        setGpsError(true);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // ============== REVERSE GEOCODING ==============
  const lastGeocodeTimeRef = useRef(0);
  useEffect(() => {
    if (!gps?.lat || !gps?.lng) return;
    
    const reverseGeocode = async () => {
      // Enforce minimum 10s between Nominatim requests (rate limit: 1 req/sec, be conservative)
      const now = Date.now();
      if (now - lastGeocodeTimeRef.current < 10000) return;
      lastGeocodeTimeRef.current = now;
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${gps.lat}&lon=${gps.lng}&zoom=16`
        );
        const data = await response.json();
        if (data?.address) {
          const { road, city, town, village, suburb, county, state } = data.address;
          const streetName = road || '';
          const cityName = city || town || village || suburb || county || '';
          if (streetName && cityName) {
            setLocationName(`${streetName}, ${cityName}`);
          } else if (streetName || cityName) {
            setLocationName(streetName || cityName);
          } else {
            setLocationName(data.display_name?.split(',').slice(0, 2).join(',') || '');
          }
          // Resolve county/state to division for GPS-based division tracking
          if (county || state) {
            setGpsDivision(resolveDivisionFromCounty(county, state));
          } else {
            setGpsDivision(TRAVEL_DIVISION);
          }
        }
      } catch (e) {
        console.error('Reverse geocode error:', e);
      }
    };
    
    // Debounce the initial call, but no repeating interval
    const timeout = setTimeout(reverseGeocode, 500);
    
    return () => {
      clearTimeout(timeout);
    };
  }, [gps?.lat, gps?.lng]);

  // ============== HEADING TO DIRECTION ==============
  const headingToTrafficDirection = useCallback((heading) => {
    if (heading === null || heading === undefined || isNaN(heading)) return null;
    // Normalize heading to 0-360
    heading = ((heading % 360) + 360) % 360;
    // Cardinal directions: N=0, E=90, S=180, W=270
    if (heading >= 315 || heading < 45) return "NB";
    if (heading >= 45 && heading < 135) return "EB";
    if (heading >= 135 && heading < 225) return "SB";
    if (heading >= 225 && heading < 315) return "WB";
    return null;
  }, []);

  // Get street name from locationName (already reverse geocoded)
  const getStreetNameFromLocation = useCallback(() => {
    if (!locationName) return null;
    // locationName is in format "Street Name, City" - extract street
    const parts = locationName.split(',');
    return parts[0]?.trim() || null;
  }, [locationName]);

  // ============== FETCH FUNCTIONS ==============
  const fetchStatus = useCallback(async () => {
    if (!user?.rangerId) return;
    try {
      const res = await api.get('/status/active-units');
      const me = (res.data || []).find(u => u.rangerId?.toUpperCase() === user.rangerId.toUpperCase());
      if (me && !statusInitialized) {
        // Only set from server on initial load
        setStatus(me.status || 'available');
        setBusyReason(me.busyReason || '');
        setStatusInitialized(true);
      }
    } catch (e) { console.error(e); }
  }, [user?.rangerId, statusInitialized]);

  const fetchActiveUnits = useCallback(async () => {
    setUnitsLoading(true);
    try {
      const res = await api.get('/status/active-units');
      setActiveUnits(res.data || []);
    } catch (e) { 
      console.error(e); 
    }
    setUnitsLoading(false);
  }, []);

  const fetchWazeAlerts = useCallback(async () => {
    setWazeLoading(true);
    try {
      const res = await api.get('/waze/alerts');
      setWazeAlerts(res.data || []);
    } catch (e) { 
      console.error(e); 
    }
    setWazeLoading(false);
  }, []);

  const fetchIncidents = useCallback(async () => {
    if (!user?.rangerId) return;
    setIncidentsLoading(true);
    try {
      // Main list — recent incidents for the active view
      const res = await api.get('/incidents', { params: { pageSize: 100 } });
      const items = res.data?.items || [];
      const active = items.filter(i => !i.occurredEndUtc);
      setIncidents(items);
      setActiveIncidents(active);
      
      // Filter to my active incidents
      const mine = items.filter(i => 
        i.status === 'open' && 
        i.primaryRangerId?.toUpperCase() === user.rangerId.toUpperCase()
      );
      setMyIncidents(mine);
      
      // Dedicated server-side query for incomplete incidents (avoids pagination gaps)
      try {
        const incRes = await api.get('/incidents', {
          params: { status: 'incomplete', rangerId: user.rangerId, pageSize: 200 }
        });
        const incItems = incRes.data?.items || [];
        const incCount = incItems.length;
        setIncompleteCount(incCount);
        if (incCount >= MAX_INCOMPLETE) {
          setIncompleteWarningOpen(true);
        }
      } catch (incErr) {
        // Fallback to client-side filter if targeted query fails
        const incomplete = items.filter(i =>
          (i.status || '').toLowerCase() === 'incomplete' &&
          i.primaryRangerId?.toUpperCase() === user.rangerId.toUpperCase()
        );
        setIncompleteCount(incomplete.length);
        if (incomplete.length >= MAX_INCOMPLETE) {
          setIncompleteWarningOpen(true);
        }
      }
    } catch (e) { 
      console.error(e); 
    }
    setIncidentsLoading(false);
  }, [user?.rangerId]);

  const loadStatusCheckSettings = useCallback(async () => {
    try {
      const response = await api.get('/status-check-settings');
      const payload = response.data || {};
      const defaultMinutes = Number(payload.defaultMinutes);
      const safeDefault = Number.isFinite(defaultMinutes)
        ? defaultMinutes
        : DEFAULT_STATUS_CHECK_MINUTES;
      const overridesArray = Array.isArray(payload.overrides) ? payload.overrides : [];
      const overrides = overridesArray.reduce((map, entry) => {
        if (!entry || !entry.incidentType) {
          return map;
        }
        const minutes = Number(entry.minutes);
        if (!Number.isFinite(minutes) || minutes <= 0) {
          return map;
        }
        map[entry.incidentType.trim().toLowerCase()] = minutes;
        return map;
      }, {});

      setStatusCheckSettings({
        defaultMinutes: safeDefault,
        overrides,
      });
    } catch (error) {
      console.error('Unable to load status check settings.', error);
    }
  }, []);

  useEffect(() => {
    loadStatusCheckSettings();
  }, [loadStatusCheckSettings]);

  const getStatusCheckThreshold = useCallback(
    (incidentType, source) => {
      return getStatusCheckThresholdUtil(incidentType, source, statusCheckSettings);
    },
    [statusCheckSettings.defaultMinutes, statusCheckSettings.overrides]
  );

  // ============== PERIODIC UPDATES ==============
  useEffect(() => {
    fetchStatus();
    fetchActiveUnits();
    fetchWazeAlerts();
    fetchIncidents();
    
    const statusInterval = setInterval(fetchStatus, 30000);
    const unitsInterval = setInterval(fetchActiveUnits, 30000);
    const wazeInterval = setInterval(fetchWazeAlerts, 120000);
    const incidentsInterval = setInterval(fetchIncidents, 30000);
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(unitsInterval);
      clearInterval(wazeInterval);
      clearInterval(incidentsInterval);
    };
  }, [fetchStatus, fetchActiveUnits, fetchWazeAlerts, fetchIncidents]);

  useEffect(() => {
    setStatusAcknowledgements((prev) => {
      const activeIds = new Set(activeIncidents.map((incident) => incident.id));
      let changed = false;
      const next = Object.entries(prev).reduce((acc, [incidentId, timestamp]) => {
        if (activeIds.has(Number(incidentId))) {
          acc[incidentId] = timestamp;
        } else {
          changed = true;
        }
        return acc;
      }, {});
      return changed ? next : prev;
    });
  }, [activeIncidents]);

  const statusCheckIncidentSet = useMemo(
    () => new Set(statusCheckIncidents),
    [statusCheckIncidents]
  );
  const statusCheckRangerSet = useMemo(() => {
    const normalized = statusCheckRangers
      .map((id) => (typeof id === 'string' ? id.toUpperCase() : null))
      .filter(Boolean);
    return new Set(normalized);
  }, [statusCheckRangers]);

  const evaluateStatusChecks = useCallback(() => {
    const now = Date.now();
    const triggeredIncidentIds = [];
    const triggeredRangerIds = new Set();
    const normalizedUser = user?.rangerId ? user.rangerId.toUpperCase() : null;
    let modalCandidate = null;

    activeIncidents.forEach((incident) => {
      if (!incident?.occurredAtUtc) {
        return;
      }

      const startedAt = Date.parse(incident.occurredAtUtc);
      if (!Number.isFinite(startedAt)) {
        return;
      }

      const minutesOnScene = (now - startedAt) / 60000;
      const thresholdMinutes = getStatusCheckThreshold(incident.incidentType, incident.source);
      if (minutesOnScene < thresholdMinutes) {
        return;
      }

      const incidentKey = String(incident.id);
      const localAck = statusAcknowledgements[incidentKey];
      const remoteAck = incident.statusCheckAcknowledgedUtc
        ? Date.parse(incident.statusCheckAcknowledgedUtc)
        : null;
      const latestAck = Math.max(
        Number.isFinite(localAck) ? localAck : 0,
        Number.isFinite(remoteAck) ? remoteAck : 0
      );

      if (latestAck > 0) {
        const minutesSinceAck = (now - latestAck) / 60000;
        if (minutesSinceAck < thresholdMinutes) {
          return;
        }
      }

      triggeredIncidentIds.push(incident.id);

      if (Array.isArray(incident.rangerIds)) {
        incident.rangerIds.forEach((rangerId) => {
          if (rangerId) {
            triggeredRangerIds.add(rangerId.toUpperCase());
          }
        });
      }

      const normalizedPrimary = incident.primaryRangerId
        ? incident.primaryRangerId.toUpperCase()
        : null;

      if (normalizedPrimary) {
        triggeredRangerIds.add(normalizedPrimary);
      }
      const isPrimaryForUser = normalizedPrimary && normalizedUser && normalizedPrimary === normalizedUser;

      if (!modalCandidate && isPrimaryForUser) {
        modalCandidate = {
          incident,
          minutes: Math.max(1, Math.floor(minutesOnScene)),
        };
      }
    });

    const pinnedIncidentId = statusModalPinnedRef.current;
    if (pinnedIncidentId) {
      const pinnedIncident = incidents.find((incident) => incident.id === pinnedIncidentId);
      if (pinnedIncident?.occurredEndUtc) {
        statusModalPinnedRef.current = null;
        statusModalHoldUntilRef.current = 0;
      } else if (pinnedIncident) {
        if (!triggeredIncidentIds.includes(pinnedIncidentId)) {
          triggeredIncidentIds.push(pinnedIncidentId);
        }
        if (Array.isArray(pinnedIncident.rangerIds)) {
          pinnedIncident.rangerIds.forEach((rangerId) => {
            if (rangerId) {
              triggeredRangerIds.add(rangerId.toUpperCase());
            }
          });
        }
        if (pinnedIncident.primaryRangerId) {
          triggeredRangerIds.add(pinnedIncident.primaryRangerId.toUpperCase());
        }
      }
    }

    setStatusCheckIncidents((prev) => {
      if (prev.length === triggeredIncidentIds.length && prev.every((id, i) => id === triggeredIncidentIds[i])) {
        return prev;
      }
      return triggeredIncidentIds;
    });
    setStatusCheckRangers((prev) => {
      const next = Array.from(triggeredRangerIds).sort();
      const prevSorted = [...prev].sort();
      if (prevSorted.length === next.length && prevSorted.every((id, i) => id === next[i])) {
        return prev;
      }
      return next;
    });
    setStatusModalIncident((prev) => {
      if (modalCandidate) {
        if (prev?.incident?.id === modalCandidate.incident.id && prev.minutes === modalCandidate.minutes) {
          return prev;
        }
        statusModalHoldUntilRef.current = now + 5000;
        statusModalPinnedRef.current = modalCandidate.incident.id;
        return modalCandidate;
      }

      if (prev && !triggeredIncidentIds.includes(prev.incident.id)) {
        if (now < statusModalHoldUntilRef.current) {
          return prev;
        }
        const ended = incidents.some(
          (incident) => incident.id === prev.incident.id && Boolean(incident.occurredEndUtc)
        );
        return ended ? null : prev;
      }

      return prev;
    });
  }, [activeIncidents, getStatusCheckThreshold, incidents, statusAcknowledgements, user?.rangerId]);

  useEffect(() => {
    evaluateStatusChecks();
    const intervalId = setInterval(evaluateStatusChecks, STATUS_CHECK_POLL_MS);
    return () => clearInterval(intervalId);
  }, [evaluateStatusChecks]);

  const stopStatusAlertAudio = useCallback(() => {
    const audio = statusAlertAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const ensureStatusAlertAudio = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    if (statusAlertAudioRef.current) {
      return statusAlertAudioRef.current;
    }

    try {
      const base = typeof process !== 'undefined' && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : '';
      const source = `${base}/sounds/statuscheckalarm.mp3`;
      const audio = new Audio(source);
      audio.loop = false;
      audio.preload = 'auto';
      statusAlertAudioRef.current = audio;
      return audio;
    } catch (error) {
      console.error('Unable to prepare status alert audio', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !statusModalOpen || !statusBackdropAlert) {
      if (statusAudioTimerRef.current) {
        clearInterval(statusAudioTimerRef.current);
        statusAudioTimerRef.current = null;
      }
      stopStatusAlertAudio();
      return undefined;
    }

    const audio = ensureStatusAlertAudio();
    if (!audio) {
      return undefined;
    }

    const playAlert = () => {
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => console.warn('Status alert audio blocked', err));
      }
    };

    playAlert();
    statusAudioTimerRef.current = window.setInterval(playAlert, STATUS_ALERT_REPEAT_MS);

    return () => {
      if (statusAudioTimerRef.current) {
        clearInterval(statusAudioTimerRef.current);
        statusAudioTimerRef.current = null;
      }
      stopStatusAlertAudio();
    };
  }, [ensureStatusAlertAudio, statusBackdropAlert, statusModalOpen, stopStatusAlertAudio]);

  useEffect(() => {
    if (!statusModalOpen) {
      setStatusBackdropAlert(false);
      if (statusBackdropTimerRef.current) {
        clearTimeout(statusBackdropTimerRef.current);
        statusBackdropTimerRef.current = null;
      }
      return undefined;
    }

    setStatusBackdropAlert(false);
    if (statusBackdropTimerRef.current) {
      clearTimeout(statusBackdropTimerRef.current);
    }

    statusBackdropTimerRef.current = setTimeout(() => setStatusBackdropAlert(true), 30000);

    return () => {
      if (statusBackdropTimerRef.current) {
        clearTimeout(statusBackdropTimerRef.current);
        statusBackdropTimerRef.current = null;
      }
    };
  }, [statusModalOpen, statusModalIncident?.incident?.id]);

  // ============== PING TO KEEP STATUS ALIVE ==============
  useEffect(() => {
    if (!user?.rangerId) return;
    
    const formatLabel = (s, reason) => {
      if (s === 'busy' && reason) return reason.toUpperCase().replace(/-/g, ' ');
      if (s === 'out-of-service') return 'OUT OF SERVICE';
      return s.toUpperCase();
    };
    
    const sendPing = async () => {
      try {
        await api.post('/status/ping', {
          status: status,
          statusLabel: formatLabel(status, busyReason),
          latitude: gps?.lat,
          longitude: gps?.lng,
          heading: gps?.heading,
          speed: gps?.speed,
          division: gpsDivision || undefined,
          isMobile: true,
        });
      } catch (e) { console.error(e); }
    };

    sendPing();
    const pingInterval = setInterval(sendPing, 15000);
    return () => clearInterval(pingInterval);
  }, [user?.rangerId, status, busyReason, gps, gpsDivision]);

  // ============== EMERGENCY AUDIO ==============
  const stopEmergencyAudio = useCallback(() => {
    const audio = emergencyAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const startEmergencyAudio = useCallback(() => {
    if (typeof window === 'undefined') return;
    stopEmergencyAudio();
    try {
      const source = getEmergencySoundPath();
      const audio = new Audio(source);
      audio.preload = 'auto';
      audio.loop = true;
      emergencyAudioRef.current = audio;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => console.warn('Emergency audio blocked', err));
      }
    } catch (error) {
      console.error('Unable to prepare emergency audio', error);
    }
  }, [stopEmergencyAudio]);

  const clearEmergencyCountdown = useCallback(() => {
    if (emergencyCountdownIntervalRef.current) {
      window.clearInterval(emergencyCountdownIntervalRef.current);
      emergencyCountdownIntervalRef.current = null;
    }
  }, []);

  const closeEmergencyConfirm = useCallback(() => {
    clearEmergencyCountdown();
    setEmergencyConfirmOpen(false);
    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
  }, [clearEmergencyCountdown]);

  const activateEmergencyStatus = useCallback(async () => {
    if (emergencyActivating) {
      return;
    }
    if (status === 'emergency') {
      setStatusNotice({ type: 'warning', message: 'YOU ARE ALREADY IN EMERGENCY STATUS' });
      return;
    }

    setEmergencyActivating(true);
    setStatusLoading(true);
    try {
      await api.post('/status/ping', {
        status: 'emergency',
        statusLabel: 'EMERGENCY',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        isMobile: true,
      });

      preEmergencyStatusRef.current = status;
      await api.post('/status/emergency', {
        latitude: gps?.lat,
        longitude: gps?.lng,
        location: locationName || (gps?.lat && gps?.lng ? `${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}` : undefined),
      });
      startEmergencyAudio();
      setStatus('emergency');
      setStatusNotice({ type: 'error', message: 'EMERGENCY ACTIVATED - HELP IS ON THE WAY' });

      await fetchActiveUnits();
    } catch (e) {
      console.error('Status update error:', e);
      setStatusNotice({ type: 'error', message: 'FAILED TO ACTIVATE EMERGENCY - TRY AGAIN' });
    } finally {
      setStatusLoading(false);
      setEmergencyActivating(false);
    }
  }, [emergencyActivating, fetchActiveUnits, gps?.heading, gps?.lat, gps?.lng, locationName, startEmergencyAudio, status]);

  const handleEmergencyPress = useCallback(() => {
    if (status === 'emergency') {
      setStatusNotice({ type: 'warning', message: 'YOU ARE ALREADY IN EMERGENCY STATUS' });
      return;
    }
    if (emergencyConfirmOpen || emergencyActivating || statusLoading) {
      return;
    }
    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
    setEmergencyConfirmOpen(true);
  }, [status, emergencyConfirmOpen, emergencyActivating, statusLoading]);

  useEffect(() => {
    if (!statusNotice) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setStatusNotice(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [statusNotice]);

  useEffect(() => {
    if (!emergencyConfirmOpen) {
      return;
    }

    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
    clearEmergencyCountdown();
    emergencyCountdownIntervalRef.current = window.setInterval(() => {
      setEmergencyCountdown((prev) => {
        if (prev <= 1) {
          clearEmergencyCountdown();
          setEmergencyConfirmOpen(false);
          setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
          void activateEmergencyStatus();
          return EMERGENCY_CONFIRM_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearEmergencyCountdown();
    };
  }, [activateEmergencyStatus, clearEmergencyCountdown, emergencyConfirmOpen]);

  useEffect(() => {
    return () => {
      clearEmergencyCountdown();
    };
  }, [clearEmergencyCountdown]);

  // ============== STATUS HANDLERS ==============
  const doStatus = async (s) => {
    if (s === 'emergency') {
      handleEmergencyPress();
      return;
    }
    if (s === status && s !== 'busy') return;
    setStatusLoading(true);
    
    const formatLabel = (st, reason) => {
      if (st === 'busy' && reason) return reason.toUpperCase().replace(/-/g, ' ');
      if (st === 'out-of-service') return 'OUT OF SERVICE';
      return st.toUpperCase();
    };
    
    try {
      // Always send a ping to update status (including for emergency)
      await api.post('/status/ping', {
        status: s,
        statusLabel: formatLabel(s, s === 'busy' ? busyReason : ''),
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        isMobile: true,
      });
      
      // Switching away from emergency, stop audio
      stopEmergencyAudio();
      
      // Play status updated sound
      if (s !== status) {
        playCue(SOUND_EVENTS.STATUS_UPDATED);
      }

      setStatus(s);
      if (s !== 'busy') setBusyReason('');
      
      // Refresh active units to show updated status
      await fetchActiveUnits();
    } catch (e) { 
      console.error('Status update error:', e); 
    }
    setStatusLoading(false);
  };

  // Self-cancel own emergency
  const handleSelfCancelEmergency = async () => {
    if (!selfCancelReason) {
      setSelfCancelError('Please select a reason.');
      return;
    }
    if (!selfCancelPin) {
      setSelfCancelError('Please enter your PIN.');
      return;
    }
    setSelfCancelLoading(true);
    setSelfCancelError('');
    try {
      await api.delete('/status/emergency/self', {
        data: { reason: selfCancelReason, pin: selfCancelPin },
      });
      setSelfCancelOpen(false);
      setSelfCancelReason('');
      setSelfCancelPin('');
      stopEmergencyAudio();
      // Restore previous status
      const restoreTo = preEmergencyStatusRef.current || 'available';
      setStatus(restoreTo);
      try {
        await api.post('/status/ping', { status: restoreTo, isMobile: true });
      } catch { /* best effort */ }
    } catch (error) {
      console.error('Failed to self-cancel emergency', error);
      const msg = error.response?.data?.message || 'Failed to cancel emergency. Check your PIN.';
      setSelfCancelError(typeof msg === 'string' ? msg : 'Failed to cancel emergency.');
    } finally {
      setSelfCancelLoading(false);
    }
  };

  const doBusyReason = async (r) => {
    setBusyReason(r);
    if (status === 'busy') {
      try {
        await api.post('/status/ping', { 
          status: 'busy', 
          statusLabel: r.toUpperCase().replace(/-/g, ' '), 
          latitude: gps?.lat, 
          longitude: gps?.lng,
          heading: gps?.heading,
          isMobile: true,
        });
      } catch (e) { console.error(e); }
    }
  };

  // ============== INCIDENT HANDLERS ==============
  const doQuickIncident = async (incidentType) => {
    if (isIncidentRestricted) {
      setIncidentCreateError('Incident creation is restricted until required core classes are completed and approved.');
      return;
    }

    setIncidentCreateError('');
    setSubmitting(true);
    try {
      const response = await api.post('/incidents', {
        incidentType: incidentType,
        occurredAtUtc: new Date().toISOString(),
        latitude: gps?.lat,
        longitude: gps?.lng,
        streetName: getStreetNameFromLocation() || undefined,
        trafficDirection: headingToTrafficDirection(gps?.heading) || undefined,
        primaryRangerId: user?.rangerId,
        participantRangerIds: user?.rangerId ? [user.rangerId] : [],
      });
      
      const newId = response.data?.id;
      if (newId) {
        await api.put(`/incidents/${newId}`, { source: 'SELF' });
      }
      
      setStatus('busy');
      setBusyReason('on-scene');
      await api.post('/status/ping', {
        status: 'busy',
        statusLabel: 'ON SCENE',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        onScene: true,
        isMobile: true,
      });
      
      fetchIncidents();
      setNavValue(0); // Go to home to see active incident
    } catch (e) {
      console.error(e);
      const message = e.response?.data?.message || e.response?.data || 'Failed to create incident.';
      setIncidentCreateError(typeof message === 'string' ? message : 'Failed to create incident.');
    }
    setSubmitting(false);
  };

  const claimWazeAlert = async (alert) => {
    if (isIncidentRestricted) {
      setIncidentCreateError('Incident creation is restricted until required core classes are completed and approved.');
      return;
    }

    setIncidentCreateError('');
    setClaimingWazeId(alert.id);
    try {
      // Get traffic direction from Waze magvar if available, otherwise use device heading
      let trafficDirection = null;
      if (alert.magvar !== undefined && alert.magvar !== null) {
        const magvar = alert.magvar;
        if (magvar >= 315 || magvar < 45) trafficDirection = "NB";
        else if (magvar >= 45 && magvar < 135) trafficDirection = "EB";
        else if (magvar >= 135 && magvar < 225) trafficDirection = "SB";
        else if (magvar >= 225 && magvar < 315) trafficDirection = "WB";
      } else {
        trafficDirection = headingToTrafficDirection(gps?.heading);
      }
      
      // Create incident from Waze alert
      const response = await api.post('/incidents', {
        incidentType: alert.type === 'ACCIDENT' ? 'Accident' : 'Assist Motorist',
        occurredAtUtc: new Date().toISOString(),
        latitude: alert.latitude,
        longitude: alert.longitude,
        location: alert.street || alert.city,
        streetName: alert.street || undefined,
        trafficDirection: trafficDirection || undefined,
        primaryRangerId: user?.rangerId,
        participantRangerIds: user?.rangerId ? [user.rangerId] : [],
      });
      
      const newId = response.data?.id;
      if (newId) {
        await api.put(`/incidents/${newId}`, { source: 'Waze' });
      }
      
      // Mark waze alert as claimed
      await api.put(`/waze/alerts/${alert.id}/claim`);
      
      setStatus('busy');
      setBusyReason('on-scene');
      await api.post('/status/ping', {
        status: 'busy',
        statusLabel: 'ON SCENE',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        onScene: true,
        isMobile: true,
      });
      
      fetchIncidents();
      fetchWazeAlerts();
      setNavValue(0); // Go to home
    } catch (e) {
      console.error(e);
      const message = e.response?.data?.message || e.response?.data || 'Failed to create incident from alert.';
      setIncidentCreateError(typeof message === 'string' ? message : 'Failed to create incident from alert.');
    }
    setClaimingWazeId(null);
  };

  // ON SCENE handler - creates incident and goes on scene
  const handleWazeOnScene = async (alert) => {
    if (isIncidentRestricted) {
      setIncidentCreateError('Incident creation is restricted until required core classes are completed and approved.');
      return;
    }

    setIncidentCreateError('');
    setClaimingWazeId(alert.id);
    try {
      // Get traffic direction from Waze magvar
      let trafficDirection = null;
      if (alert.magvar !== null && alert.magvar !== undefined) {
        const heading = ((alert.magvar % 360) + 360) % 360;
        if (heading >= 315 || heading < 45) trafficDirection = "NB";
        else if (heading >= 45 && heading < 135) trafficDirection = "EB";
        else if (heading >= 135 && heading < 225) trafficDirection = "SB";
        else if (heading >= 225 && heading < 315) trafficDirection = "WB";
      }

      const response = await api.post(`/waze/alerts/${alert.id}/convert`, {
        onScene: true,
        trafficDirection,
      });
      
      await fetchWazeAlerts();
      await fetchIncidents();
      
      // Update status to on-scene
      setStatus('busy');
      setBusyReason('on-scene');
      await api.post('/status/ping', {
        status: 'busy',
        statusLabel: 'ON SCENE',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        onScene: true,
        isMobile: true,
      });
      
      // Open edit dialog for the new incident
      const newIncidentId = response.data?.incidentId;
      if (newIncidentId) {
        const incRes = await api.get(`/incidents/${newIncidentId}`);
        if (incRes.data) {
          openEditDialog(incRes.data);
        }
      }
    } catch (e) {
      console.error("ON SCENE error:", e);
      const message = e.response?.data?.message || e.response?.data || 'Failed to create incident from alert.';
      setIncidentCreateError(typeof message === 'string' ? message : 'Failed to create incident from alert.');
    }
    setClaimingWazeId(null);
  };

  // UTL (Unable To Locate) handler - clears the alert
  const handleWazeUtl = async (alertId) => {
    setClaimingWazeId(alertId);
    try {
      await api.post(`/waze/alerts/${alertId}/clear`, { reason: "UTL" });
      await fetchWazeAlerts();
    } catch (e) {
      console.error("UTL error:", e);
    }
    setClaimingWazeId(null);
  };

  // Filter handlers with localStorage persistence
  const handleWazeTypeToggle = (type) => {
    setWazeTypeFilter(prev => {
      const next = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type];
      if (typeof window !== "undefined") {
        window.localStorage.setItem("mobileWazeTypeFilter", JSON.stringify(next));
      }
      return next;
    });
  };

  const handleWazeCityToggle = (city) => {
    setWazeCityFilter(prev => {
      const next = prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city];
      if (typeof window !== "undefined") {
        window.localStorage.setItem("mobileWazeCityFilter", JSON.stringify(next));
      }
      // Clear distance filter when using city filter
      if (next.length > 0) {
        setWazeDistanceFilter(0);
        window.localStorage.setItem("mobileWazeDistanceFilter", JSON.stringify(0));
      }
      return next;
    });
  };

  const handleWazeDistanceChange = (distance) => {
    setWazeDistanceFilter(distance);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mobileWazeDistanceFilter", JSON.stringify(distance));
    }
    // Clear city filter when using distance filter
    if (distance > 0) {
      setWazeCityFilter([]);
      window.localStorage.setItem("mobileWazeCityFilter", JSON.stringify([]));
    }
  };

  const clearAllWazeFilters = () => {
    setWazeCityFilter([]);
    setWazeTypeFilter([]);
    setWazeDistanceFilter(5); // Default to 5 miles
    if (typeof window !== "undefined") {
      window.localStorage.setItem("mobileWazeCityFilter", JSON.stringify([]));
      window.localStorage.setItem("mobileWazeTypeFilter", JSON.stringify([]));
      window.localStorage.setItem("mobileWazeDistanceFilter", JSON.stringify(5));
    }
  };

  const toggleFollowMe = () => {
    setFollowMeEnabled(prev => {
      const next = !prev;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("mobileFollowMe", JSON.stringify(next));
      }
      return next;
    });
  };

  const getWazeTypeLabel = (alert) => {
    const category = getWazeAlertCategory(alert.type, alert.subtype);
    switch (category) {
      case "ASSIST_MOTORIST": return "ASSIST MOTORIST";
      case "DEBRIS": return "DEBRIS";
      case "ACCIDENT": return "ACCIDENT";
      case "ASSIST_OTHER_AGENCY": return "ASSIST OTHER AGENCY";
      default: return alert.type?.replace(/_/g, " ") || "UNKNOWN";
    }
  };

  const openEditDialog = (inc) => {
    setEditIncident(inc);
    setEditForm({
      services: inc.servicesProvided || [],
      notes: inc.notes || '',
      vehicle: {
        year: inc.vehicle?.year || '',
        make: inc.vehicle?.make || '',
        model: inc.vehicle?.model || '',
        color: inc.vehicle?.color || '',
        state: inc.vehicle?.state || '',
        plate: inc.vehicle?.plate || '',
      },
    });
    setEditErrors([]);
    setEditOpen(true);
  };

  const doSaveAndClear = async () => {
    if (!editIncident) return;
    
    const missing = [];
    if (!editForm.notes?.trim()) missing.push('notes');
    if (!editForm.services?.length) missing.push('disposition');
    
    if (missing.length > 0) {
      setEditErrors(missing);
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/incidents/${editIncident.id}`, {
        servicesProvided: editForm.services,
        notes: editForm.notes,
        vehicle: editForm.vehicle,
        status: 'closed',
        occurredEndUtc: new Date().toISOString(),
      });
      
      setStatus('available');
      setBusyReason('');
      await api.post('/status/ping', {
        status: 'available',
        statusLabel: 'AVAILABLE',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        onScene: false,
        isMobile: true,
      });
      
      setEditOpen(false);
      setEditIncident(null);
      fetchIncidents();
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const doFinishLater = async () => {
    if (!editIncident) return;
    if (incompleteCount >= MAX_INCOMPLETE) {
      setIncompleteWarningOpen(true);
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/incidents/${editIncident.id}`, {
        servicesProvided: editForm.services,
        notes: editForm.notes,
        vehicle: editForm.vehicle,
        status: 'incomplete',
        occurredEndUtc: new Date().toISOString(),
      });
      
      setStatus('available');
      setBusyReason('');
      await api.post('/status/ping', {
        status: 'available',
        statusLabel: 'AVAILABLE',
        latitude: gps?.lat,
        longitude: gps?.lng,
        heading: gps?.heading,
        onScene: false,
        isMobile: true,
      });
      
      setEditOpen(false);
      setEditIncident(null);
      fetchIncidents();
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const acknowledgeIncidentStatus = useCallback(async (incidentId) => {
    if (!incidentId) {
      return false;
    }

    const key = String(incidentId);
    const timestamp = Date.now();
    setStatusAcknowledgements((prev) => {
      if (prev[key] === timestamp) {
        return prev;
      }
      return { ...prev, [key]: timestamp };
    });

    try {
      await api.post(`/incidents/${incidentId}/status-check/ack`);
      return true;
    } catch (error) {
      console.error('Unable to acknowledge status check.', error);
      return false;
    }
  }, []);

  const handleStatusCheckAcknowledge = useCallback(async () => {
    const incidentId = statusModalIncident?.incident?.id;
    if (!incidentId) {
      return;
    }

    const acknowledged = await acknowledgeIncidentStatus(incidentId);
    if (acknowledged) {
      statusModalPinnedRef.current = null;
      setStatusModalIncident(null);
    }
  }, [acknowledgeIncidentStatus, statusModalIncident]);

  const handleStatusCheckClear = useCallback(async () => {
    const incidentId = statusModalIncident?.incident?.id;
    if (!incidentId) {
      return;
    }

    const acknowledged = await acknowledgeIncidentStatus(incidentId);
    if (acknowledged) {
      statusModalPinnedRef.current = null;
      const incident = statusModalIncident?.incident;
      setStatusModalIncident(null);
      if (incident) {
        openEditDialog(incident);
      }
    }
  }, [acknowledgeIncidentStatus, statusModalIncident]);

  const toggleService = (service) => {
    setEditForm(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
    setEditErrors(prev => prev.filter(e => e !== 'disposition'));
  };

  // ============== EXPENSE HANDLERS ==============
  const handleExpenseChange = (field) => (e) => {
    setExpenseForm(prev => ({ ...prev, [field]: e.target.value }));
    setExpenseError(null);
  };

  const handleExpenseReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setExpenseError('Invalid file type. Allowed: jpg, png, gif, webp, pdf');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setExpenseError('File size exceeds 10MB limit.');
        return;
      }
      setExpenseReceipt(file);
      setExpenseError(null);
    }
  };

  const handleExpenseSubmit = async () => {
    setExpenseError(null);
    setExpenseSubmitting(true);

    try {
      if (!expenseForm.purchaseDate || !expenseForm.category || !expenseForm.itemDescription || !expenseForm.amountDollars) {
        setExpenseError('Please fill in all required fields.');
        setExpenseSubmitting(false);
        return;
      }

      const selectedCategory = EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category));
      if (selectedCategory?.requiresNotes && !expenseForm.notes) {
        setExpenseError('Notes are required for this category.');
        setExpenseSubmitting(false);
        return;
      }

      const amountCents = Math.round(parseFloat(expenseForm.amountDollars) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setExpenseError('Please enter a valid amount.');
        setExpenseSubmitting(false);
        return;
      }

      const expenseData = {
        purchaseDate: expenseForm.purchaseDate,
        category: parseInt(expenseForm.category),
        itemDescription: expenseForm.itemDescription,
        amountCents,
        notes: expenseForm.notes || null,
      };

      const response = await api.post('/expenses', expenseData, { skipAutoLogout: true });
      const createdExpense = response.data;

      if (expenseReceipt && createdExpense.id) {
        const formData = new FormData();
        formData.append('file', expenseReceipt);
        try {
          await api.post(`/expenses/${createdExpense.id}/receipt`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            skipAutoLogout: true,
          });
        } catch (receiptError) {
          console.error('Receipt upload failed:', receiptError);
        }
      }

      setExpenseSuccess(true);
      setExpenseForm({
        purchaseDate: new Date().toISOString().split('T')[0],
        category: '',
        itemDescription: '',
        amountDollars: '',
        notes: '',
      });
      setExpenseReceipt(null);
      const fileInput = document.getElementById('mobile-expense-receipt');
      if (fileInput) fileInput.value = '';

      setTimeout(() => {
        setExpenseSuccess(false);
        setExpenseDialogOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Expense submission error:', error);
      // Handle error response - ensure we always set a string, not an object
      let errorMessage = 'Failed to submit expense.';
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.title) {
          errorMessage = error.response.data.title;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setExpenseError(errorMessage);
    } finally {
      setExpenseSubmitting(false);
    }
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      purchaseDate: new Date().toISOString().split('T')[0],
      category: '',
      itemDescription: '',
      amountDollars: '',
      notes: '',
    });
    setExpenseReceipt(null);
    setExpenseError(null);
    setExpenseSuccess(false);
  };

  // ============== FILTERED DATA ==============
  const filteredWazeAlerts = useMemo(() => {
    let filtered = wazeAlerts;
    
    // Filter by type
    if (wazeTypeFilter.length > 0) {
      filtered = filtered.filter(alert => {
        const category = getWazeAlertCategory(alert.type, alert.subtype);
        return wazeTypeFilter.includes(category);
      });
    }
    
    // Filter by distance (requires GPS)
    if (wazeDistanceFilter > 0 && gps?.lat && gps?.lng) {
      filtered = filtered.map(alert => ({
        ...alert,
        distanceMiles: calculateDistanceMiles(gps.lat, gps.lng, alert.latitude, alert.longitude)
      })).filter(alert => 
        alert.distanceMiles !== null && alert.distanceMiles <= wazeDistanceFilter
      );
    }
    
    // Filter by city (only if distance filter not active)
    if (wazeDistanceFilter === 0 && wazeCityFilter.length > 0) {
      filtered = filtered.filter(alert =>
        wazeCityFilter.some(city => alert.city?.toLowerCase().includes(city.toLowerCase()))
      );
    }
    
    // Add distance to all alerts if GPS available
    if (gps?.lat && gps?.lng) {
      filtered = filtered.map(alert => ({
        ...alert,
        distanceMiles: alert.distanceMiles ?? calculateDistanceMiles(gps.lat, gps.lng, alert.latitude, alert.longitude)
      })).sort((a, b) => (a.distanceMiles || 999) - (b.distanceMiles || 999));
    }
    
    return filtered;
  }, [wazeAlerts, wazeCityFilter, wazeTypeFilter, wazeDistanceFilter, gps]);

  // Nearby alerts (within 0.25 miles) for ON SCENE/UTL buttons
  const nearbyWazeAlerts = useMemo(() => {
    if (!gps?.lat || !gps?.lng) return [];
    return filteredWazeAlerts.filter(alert =>
      alert.distanceMiles !== null && alert.distanceMiles <= 0.25 && alert.status === "active"
    );
  }, [filteredWazeAlerts, gps]);

  // Get active filter count for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (wazeCityFilter.length > 0) count++;
    if (wazeTypeFilter.length > 0) count++;
    if (wazeDistanceFilter > 0) count++;
    return count;
  }, [wazeCityFilter, wazeTypeFilter, wazeDistanceFilter]);

  const sortedActiveUnits = useMemo(() => {
    return [...activeUnits].sort((a, b) => {
      const aStale = Boolean(a?.isStale);
      const bStale = Boolean(b?.isStale);
      if (aStale !== bStale) {
        return aStale ? 1 : -1;
      }

      const statusOrder = { emergency: 0, busy: 1, available: 2, 'out-of-service': 3 };
      const aOrder = statusOrder[a.status] ?? 4;
      const bOrder = statusOrder[b.status] ?? 4;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.rangerId || '').localeCompare(b.rangerId || '');
    });
  }, [activeUnits]);

  const mobileStatusCheckPulse = {
    animation: 'mobileStatusCheckPulse 1.6s ease-in-out infinite',
    '@keyframes mobileStatusCheckPulse': {
      '0%': { backgroundColor: 'rgba(255, 213, 79, 0)' },
      '40%': { backgroundColor: 'rgba(255, 213, 79, 0.45)' },
      '80%': { backgroundColor: 'rgba(255, 213, 79, 0)' },
      '100%': { backgroundColor: 'rgba(255, 213, 79, 0)' },
    },
  };

  // ============== RENDER FUNCTIONS ==============

  // HOME VIEW - Status + Active Incident
  const renderHomeView = () => (
    <Stack spacing={2}>
      {/* GPS Status Banner */}
      <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {gpsError ? (
          <>
            <GpsOffIcon color="error" />
            <Typography variant="body2" color="error">GPS Unavailable</Typography>
          </>
        ) : gps ? (
          <>
            <GpsFixedIcon color="success" />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
              {locationName || `${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}`}
            </Typography>
            <Chip 
              size="small" 
              label={`${Math.round(gps.accuracy || 0)}m`} 
              color={gps.accuracy < 50 ? 'success' : 'warning'}
            />
          </>
        ) : (
          <>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Acquiring GPS...</Typography>
          </>
        )}
      </Paper>

      {/* Status Section */}
      <Paper sx={{ 
        p: 2,
        position: "relative",
        overflow: "hidden",
        border: snowbankEnabled ? "2px solid rgba(79, 195, 247, 0.6)" : "none",
        // Snowbank effect
        ...(snowbankEnabled && {
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "14px",
            background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(220,235,250,0.8) 40%, rgba(200,220,245,0.4) 70%, transparent 100%)",
            borderRadius: "8px 8px 50% 50% / 8px 8px 100% 100%",
            filter: "blur(1px)",
            zIndex: 10,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "-4px",
            left: "5%",
            right: "5%",
            height: "20px",
            background: "radial-gradient(ellipse at 20% 50%, rgba(255,255,255,0.9) 0%, transparent 50%), radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.95) 0%, transparent 45%), radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.9) 0%, transparent 50%)",
            filter: "blur(2px)",
            zIndex: 11,
            pointerEvents: "none",
          },
        }),
      }}>
        <Typography variant="h6" fontWeight={800} textAlign="center" sx={{ mb: 2 }}>
          YOUR STATUS
        </Typography>

        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant={status === 'available' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => doStatus('available')}
              disabled={statusLoading}
              sx={{ height: 60, fontSize: '1rem', fontWeight: 800, borderWidth: 2 }}
            >
              {getTenCodeLabel('available', useTenCodes) || 'AVAILABLE'}
            </Button>
            <Button
              fullWidth
              variant={status === 'busy' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => doStatus('busy')}
              disabled={statusLoading}
              sx={{ height: 60, fontSize: '1rem', fontWeight: 800, borderWidth: 2 }}
            >
              {getTenCodeLabel('busy', useTenCodes) || 'BUSY'}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant={status === 'out-of-service' ? 'contained' : 'outlined'}
              color="inherit"
              onClick={() => doStatus('out-of-service')}
              disabled={statusLoading}
              sx={{ height: 60, fontSize: '0.85rem', fontWeight: 800, borderWidth: 2 }}
            >
              {getTenCodeLabel('out-of-service', useTenCodes) || 'OUT OF SERVICE'}
            </Button>
            <Button
              fullWidth
              variant={status === 'emergency' ? 'contained' : 'outlined'}
              color="error"
              onClick={handleEmergencyPress}
              disabled={statusLoading}
              sx={{ height: 60, fontSize: '1rem', fontWeight: 800, borderWidth: 2 }}
            >
              {getTenCodeLabel('emergency', useTenCodes) || 'EMERGENCY'}
            </Button>
          </Stack>
        </Stack>

        {statusNotice && (
          <Alert severity={statusNotice.type} sx={{ mt: 2, textTransform: 'uppercase' }}>
            {statusNotice.message}
          </Alert>
        )}

        {/* Emergency Self-Cancel Banner */}
        {status === 'emergency' && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.9)',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 900, textTransform: 'uppercase', mb: 1 }}>
              ⚠️ YOUR EMERGENCY IS ACTIVE
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setSelfCancelReason('');
                setSelfCancelPin('');
                setSelfCancelError('');
                setSelfCancelOpen(true);
              }}
              sx={{
                backgroundColor: 'white',
                color: '#e65100',
                fontWeight: 700,
                textTransform: 'uppercase',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              CANCEL MY EMERGENCY
            </Button>
          </Box>
        )}

        {/* Busy Reason Selector */}
        {status === 'busy' && myIncidents.length === 0 && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Busy Reason</InputLabel>
            <Select
              value={busyReason}
              label="Busy Reason"
              onChange={(e) => doBusyReason(e.target.value)}
              sx={{ height: 50 }}
            >
              {BUSY_REASONS.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Paper>

      {/* Active Incident Card */}
      {myIncidents.length > 0 && (
        <Paper sx={{ p: 2, bgcolor: 'error.dark', color: 'white' }}>
          <Typography variant="h6" fontWeight={800} textAlign="center" sx={{ mb: 2 }}>
            ACTIVE INCIDENT
          </Typography>
          
          {myIncidents.map(inc => (
            <Card key={inc.id} sx={{ mb: 1, ...(statusCheckIncidentSet.has(inc.id) ? mobileStatusCheckPulse : null) }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {inc.incidentType?.replace(/-/g, ' ').toUpperCase() || 'INCIDENT'}
                  </Typography>
                  {inc.occurredAtUtc && (
                    <Typography variant="caption" color="text.secondary">
                      {formatUtcToLocal(inc.occurredAtUtc, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  )}
                </Stack>
                {inc.location && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {inc.location}
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => openEditDialog(inc)}
                  sx={{ fontWeight: 700 }}
                >
                  EDIT & CLEAR
                </Button>
              </CardContent>
            </Card>
          ))}
        </Paper>
      )}

      {/* Active Units List */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={800}>
            ACTIVE UNITS ({activeUnits.length})
          </Typography>
          <IconButton size="small" onClick={fetchActiveUnits} disabled={unitsLoading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {unitsLoading && activeUnits.length === 0 ? (
          <Stack spacing={0.5}>
            {[1,2,3].map(i => <Skeleton key={i} height={44} />)}
          </Stack>
        ) : activeUnits.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            No units online
          </Typography>
        ) : (
          <List disablePadding dense>
            {sortedActiveUnits.map((unit, idx) => {
              const unitNeedsStatusCheck = statusCheckRangerSet.has(unit.rangerId?.toUpperCase());
              const isStale = Boolean(unit?.isStale);
              const rowSx = {
                px: 0,
                py: 0.75,
                ...(unitNeedsStatusCheck ? mobileStatusCheckPulse : null),
                ...(isStale ? { opacity: 0.6, backgroundColor: 'action.hover' } : null),
              };
              return (
                <React.Fragment key={unit.rangerId || idx}>
                  {idx > 0 && <Divider />}
                  <ListItem sx={rowSx}>
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar sx={{ 
                        bgcolor: isStale ? 'grey.500' : `${getStatusColor(unit.status)}.main`,
                        width: 32, height: 32
                      }}>
                        {getStatusIcon(unit.status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={700} sx={isStale ? { color: 'text.disabled' } : undefined}>
                            {unit.rangerId || 'Unknown'}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={(unit.statusLabel || unit.status || '').toUpperCase()} 
                            color={getStatusColor(unit.status)}
                            sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                          />
                          {unitNeedsStatusCheck && (
                            <Chip
                              size="small"
                              label="CHECK STATUS"
                              color="warning"
                              sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {unit.lastSeenUtc && (
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto' }}>
                              <Typography variant="caption" color={isStale ? 'text.disabled' : 'text.secondary'}>
                                {formatUtcToLocal(unit.lastSeenUtc, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </Typography>
                              {isStale && (
                                <Chip
                                  size="small"
                                  label="STALE"
                                  color="default"
                                  sx={{ fontWeight: 700, height: 18, fontSize: '0.62rem' }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      }
                      secondary={(unit.incidentType || unit.activeIncidentType) ? (
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="warning.main" fontWeight={600}>
                            {(unit.incidentType || unit.activeIncidentType || '').toUpperCase()}
                          </Typography>
                          {unit.incidentLocation && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              📍 {unit.incidentLocation}
                            </Typography>
                          )}
                          {(unit.incidentLatitude && unit.incidentLongitude) && (
                            <Button
                              size="small"
                              variant="contained"
                              color="info"
                              startIcon={<NavigationIcon />}
                              onClick={() => {
                                const lat = unit.incidentLatitude;
                                const lng = unit.incidentLongitude;
                                const label = encodeURIComponent(unit.incidentLocation || 'Incident Location');
                                const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
                                const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                                window.open(wazeUrl, '_blank');
                              }}
                              sx={{ 
                                mt: 0.5, 
                                py: 0.25, 
                                px: 1,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                minHeight: 24
                              }}
                            >
                              ROUTE TO
                            </Button>
                          )}
                        </Stack>
                      ) : null}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Incomplete Warning */}
      {incompleteCount > 0 && (
        <Alert severity="warning" sx={{ fontWeight: 600 }}>
          You have {incompleteCount} incomplete incident{incompleteCount > 1 ? 's' : ''}. Complete on desktop.
        </Alert>
      )}
    </Stack>
  );

  // DISPATCH VIEW - Map-Centered Waze Alerts with filtering and ON SCENE/UTL buttons
  const isDarkMode = mode === 'dark';
  const tileConfig = isDarkMode ? DARK_TILES : LIGHT_TILES;
  const userPosition = gps ? [gps.lat, gps.lng] : null;
  
  const renderDispatchView = () => (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Full-screen Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {wazeLoading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, left: 0, right: 0, bottom: 0, 
            bgcolor: 'rgba(0,0,0,0.3)', 
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CircularProgress />
          </Box>
        )}
        <MapContainer
          center={userPosition || DEFAULT_CENTER}
          zoom={userPosition ? FOLLOW_ZOOM : DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            key={isDarkMode ? 'dark' : 'light'}
            url={tileConfig.url}
            attribution={tileConfig.attribution}
            maxZoom={19}
          />
          
          {/* Follow user location */}
          <FollowUserLocation position={userPosition} shouldFollow={followMeEnabled && !!userPosition} />
          
          {/* User location marker (blue dot) */}
          {userPosition && (
            <>
              <CircleMarker
                center={userPosition}
                radius={30}
                pathOptions={{
                  fillColor: '#4285F4',
                  fillOpacity: 0.15,
                  color: '#4285F4',
                  weight: 1,
                  opacity: 0.3
                }}
              />
              <CircleMarker
                center={userPosition}
                radius={8}
                pathOptions={{
                  fillColor: '#4285F4',
                  fillOpacity: 1,
                  color: '#ffffff',
                  weight: 3,
                  opacity: 1
                }}
              >
                <Popup>
                  <strong>Your Location</strong>
                  <div>{locationName || 'GPS Active'}</div>
                  {gps?.accuracy && <div>Accuracy: {Math.round(gps.accuracy)}m</div>}
                </Popup>
              </CircleMarker>
            </>
          )}
          
          {/* Waze alert markers */}
          {filteredWazeAlerts.map(alert => {
            if (!Number.isFinite(alert.latitude) || !Number.isFinite(alert.longitude)) return null;
            const category = getWazeAlertCategory(alert.type, alert.subtype);
            const categoryLabels = {
              ASSIST_MOTORIST: "Assist",
              DEBRIS: "Debris",
              ACCIDENT: "Accident",
              ASSIST_OTHER_AGENCY: "Agency",
            };
            return (
              <Marker
                key={alert.id}
                position={[alert.latitude, alert.longitude]}
                icon={createMobileWazeIcon(category, isDarkMode, categoryLabels[category] || alert.type)}
              >
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <strong style={{ color: WAZE_CATEGORY_COLORS[category] || '#6b7280', fontSize: 14 }}>
                      {getWazeTypeLabel(alert)}
                    </strong>
                    <div style={{ marginTop: 4 }}>
                      <strong>Location:</strong> {[alert.street, alert.city].filter(Boolean).join(', ') || 'Unknown'}
                    </div>
                    {alert.distanceMiles != null && (
                      <div><strong>Distance:</strong> {alert.distanceMiles.toFixed(2)} mi</div>
                    )}
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleWazeOnScene(alert)}
                        disabled={claimingWazeId === alert.id || isIncidentRestricted}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          backgroundColor: '#16a34a',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        {claimingWazeId === alert.id ? '...' : 'ON SCENE'}
                      </button>
                      <button
                        onClick={() => handleWazeUtl(alert.id)}
                        disabled={claimingWazeId === alert.id}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                          color: isDarkMode ? '#f3f4f6' : '#111827',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        {claimingWazeId === alert.id ? '...' : 'UTL'}
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
        
        {/* Top Controls - Filter button and alert count */}
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            position: 'absolute', 
            top: 12, 
            left: 12, 
            right: 12,
            zIndex: 1000 
          }}
        >
          <Chip
            label={`${filteredWazeAlerts.length} alerts`}
            size="small"
            color="error"
            sx={{ fontWeight: 700 }}
          />
          {wazeDistanceFilter > 0 && (
            <Chip
              label={`Within ${wazeDistanceFilter} mi`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setWazeConfigOpen(true)}
            size="small"
            sx={{ 
              bgcolor: 'background.paper', 
              boxShadow: 2,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Badge badgeContent={activeFilterCount} color="primary">
              <TuneIcon />
            </Badge>
          </IconButton>
        </Stack>
        
        {/* Bottom Controls - Follow me and recenter */}
        <Stack 
          direction="row" 
          spacing={1}
          sx={{ 
            position: 'absolute', 
            bottom: nearbyWazeAlerts.length > 0 ? 180 : 16, 
            right: 12,
            zIndex: 1000 
          }}
        >
          <Button
            variant={followMeEnabled ? "contained" : "outlined"}
            color={followMeEnabled ? "primary" : "inherit"}
            size="small"
            onClick={toggleFollowMe}
            startIcon={<MyLocationIcon />}
            sx={{ 
              bgcolor: followMeEnabled ? undefined : 'background.paper',
              fontWeight: 700,
              boxShadow: 2,
              textTransform: 'uppercase',
              fontSize: '0.7rem'
            }}
          >
            {followMeEnabled ? 'Follow' : 'Off'}
          </Button>
          <IconButton
            onClick={fetchWazeAlerts}
            disabled={wazeLoading}
            size="small"
            sx={{ 
              bgcolor: 'background.paper', 
              boxShadow: 2,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
        
        {/* Nearby Alert Panel - Shows ON SCENE / UTL buttons when within 0.25 miles */}
        {nearbyWazeAlerts.length > 0 && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              right: 12,
              zIndex: 1000,
              p: 2,
              borderRadius: 2,
              animation: 'pulse 2s ease-in-out infinite',
              "@keyframes pulse": {
                "0%, 100%": { boxShadow: "0 0 0 0 rgba(251, 191, 36, 0.4)" },
                "50%": { boxShadow: "0 0 0 8px rgba(251, 191, 36, 0)" },
              },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'warning.main', display: 'block', mb: 1 }}>
              🚨 NEARBY ALERT{nearbyWazeAlerts.length > 1 ? 'S' : ''} ({nearbyWazeAlerts.length})
            </Typography>
            <Stack spacing={1.5}>
              {nearbyWazeAlerts.slice(0, 2).map(alert => {
                const isClaiming = claimingWazeId === alert.id;
                return (
                  <Box key={alert.id}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {getWazeTypeLabel(alert)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {alert.distanceMiles?.toFixed(2)} mi
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mb: 1 }} noWrap>
                      {alert.street || 'Unknown location'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        disabled={isClaiming || isIncidentRestricted}
                        onClick={() => handleWazeOnScene(alert)}
                        sx={{ fontWeight: 700, py: 1.5 }}
                      >
                        {isClaiming ? '...' : 'ON SCENE'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        disabled={isClaiming}
                        onClick={() => handleWazeUtl(alert.id)}
                        sx={{ fontWeight: 700, py: 1.5 }}
                      >
                        {isClaiming ? '...' : 'UTL'}
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}
      </Box>
      
      {/* Filter Configuration Dialog */}
      <Dialog
        open={wazeConfigOpen}
        onClose={() => setWazeConfigOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>Alert Filters</Typography>
          <IconButton onClick={() => setWazeConfigOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Distance Filter */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Distance Filter
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {gps ? 'Filter alerts by distance from your location' : '⚠️ Enable GPS to use distance filter'}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {WAZE_DISTANCE_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => handleWazeDistanceChange(opt.value)}
                    color={wazeDistanceFilter === opt.value ? 'primary' : 'default'}
                    variant={wazeDistanceFilter === opt.value ? 'filled' : 'outlined'}
                    disabled={!gps && opt.value > 0}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Box>
            
            {/* Type Filter */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Incident Type
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {WAZE_TYPE_OPTIONS.map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => handleWazeTypeToggle(opt.value)}
                    color={wazeTypeFilter.includes(opt.value) ? 'primary' : 'default'}
                    variant={wazeTypeFilter.includes(opt.value) ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Box>
            
            {/* City Filter - only show if distance filter is off */}
            {wazeDistanceFilter === 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  City Filter
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {WAZE_CITY_OPTIONS.filter(c => c !== 'All Cities').map(city => (
                    <Chip
                      key={city}
                      label={city}
                      onClick={() => handleWazeCityToggle(city)}
                      color={wazeCityFilter.includes(city) ? 'primary' : 'default'}
                      variant={wazeCityFilter.includes(city) ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearAllWazeFilters} color="inherit">
            Reset Filters
          </Button>
          <Button onClick={() => setWazeConfigOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );

  const statusBackdropStyles = useMemo(() => {
    if (!statusBackdropAlert) {
      return { backgroundColor: 'rgba(0, 0, 0, 0.65)' };
    }
    return {
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      animation: 'statusBackdropPulse 1.15s ease-in-out infinite',
      '@keyframes statusBackdropPulse': {
        '0%': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
        '50%': { backgroundColor: 'rgba(250, 204, 21, 0.45)' },
        '100%': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
      },
    };
  }, [statusBackdropAlert]);

  // UNITS VIEW - Active Units List
  const renderUnitsView = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            ACTIVE UNITS ({activeUnits.length})
          </Typography>
          <IconButton onClick={fetchActiveUnits} disabled={unitsLoading}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {unitsLoading ? (
          <Stack spacing={1}>
            {[1,2,3,4].map(i => <Skeleton key={i} height={60} />)}
          </Stack>
        ) : activeUnits.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            No units currently online
          </Typography>
        ) : (
          <List disablePadding>
            {sortedActiveUnits.map((unit, idx) => {
              const unitNeedsStatusCheck = statusCheckRangerSet.has(unit.rangerId?.toUpperCase());
              const isStale = Boolean(unit?.isStale);
              const rowSx = {
                px: 0,
                py: 1.5,
                ...(unitNeedsStatusCheck ? mobileStatusCheckPulse : null),
                ...(isStale ? { opacity: 0.6, backgroundColor: 'action.hover' } : null),
              };
              return (
                <React.Fragment key={unit.rangerId || idx}>
                  {idx > 0 && <Divider />}
                  <ListItem sx={rowSx}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: isStale ? 'grey.500' : `${getStatusColor(unit.status)}.main`,
                        width: 44, height: 44
                      }}>
                        {getStatusIcon(unit.status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight={700} sx={isStale ? { color: 'text.disabled' } : undefined}>
                          {unit.rangerId || 'Unknown'}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip 
                            size="small" 
                            label={(unit.statusLabel || unit.status?.toUpperCase())} 
                            color={getStatusColor(unit.status)}
                            sx={{ fontWeight: 600, height: 22 }}
                          />
                          {unitNeedsStatusCheck && (
                            <Chip
                              size="small"
                              label="CHECK STATUS"
                              color="warning"
                              sx={{ fontWeight: 700, height: 22 }}
                            />
                          )}
                          {unit.latitude && unit.longitude && (
                            <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          )}
                          {unit.lastSeenUtc && (
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto' }}>
                              <Typography variant="caption" color={isStale ? 'text.disabled' : 'text.secondary'}>
                                {formatUtcToLocal(unit.lastSeenUtc, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </Typography>
                              {isStale && (
                                <Chip
                                  size="small"
                                  label="STALE"
                                  color="default"
                                  sx={{ fontWeight: 700, height: 18, fontSize: '0.62rem' }}
                                />
                              )}
                            </Stack>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Stack>
  );

  // EXPENSE VIEW - Inline expense form on mobile
  const renderExpenseView = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          LOG EXPENSE
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600}>
            For transparency only. No reimbursement provided.
          </Typography>
        </Alert>

        {expenseSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Expense logged successfully!
          </Alert>
        )}

        {expenseError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {expenseError}
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* Date */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Date *"
              type="date"
              value={expenseForm.purchaseDate}
              onChange={handleExpenseChange('purchaseDate')}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
              size="small"
            />
            <Button
              variant="outlined"
              onClick={() => setExpenseForm(prev => ({
                ...prev,
                purchaseDate: new Date().toISOString().split('T')[0]
              }))}
              sx={{ minWidth: 80, fontWeight: 700 }}
            >
              TODAY
            </Button>
          </Box>

          {/* Category */}
          <FormControl fullWidth required size="small">
            <InputLabel>Category *</InputLabel>
            <Select
              value={expenseForm.category}
              label="Category *"
              onChange={handleExpenseChange('category')}
            >
              {EXPENSE_CATEGORIES.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {cat.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cat.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Item Description */}
          <TextField
            label="Item(s) Purchased *"
            value={expenseForm.itemDescription}
            onChange={handleExpenseChange('itemDescription')}
            required
            fullWidth
            size="small"
            placeholder="e.g., 2x LED flares, jump cables"
            inputProps={{ maxLength: 512 }}
          />

          {/* Amount */}
          <TextField
            label="Amount ($) *"
            type="number"
            value={expenseForm.amountDollars}
            onChange={handleExpenseChange('amountDollars')}
            required
            fullWidth
            size="small"
            inputProps={{ min: 0.01, step: 0.01 }}
            placeholder="0.00"
          />

          {/* Notes */}
          <TextField
            label={EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category))?.requiresNotes
              ? "Notes * (required)"
              : "Notes (optional)"
            }
            value={expenseForm.notes}
            onChange={handleExpenseChange('notes')}
            required={EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category))?.requiresNotes}
            fullWidth
            size="small"
            multiline
            rows={2}
            inputProps={{ maxLength: 1024 }}
          />

          {/* Receipt Upload */}
          <Box>
            <input
              id="mobile-expense-receipt-inline"
              type="file"
              accept="image/*,.pdf"
              onChange={handleExpenseReceiptChange}
              style={{ display: 'none' }}
              capture="environment"
            />
            <label htmlFor="mobile-expense-receipt-inline">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ fontWeight: 600 }}
              >
                {expenseReceipt ? 'CHANGE RECEIPT' : 'UPLOAD RECEIPT (OPTIONAL)'}
              </Button>
            </label>
            {expenseReceipt && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                  {expenseReceipt.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setExpenseReceipt(null);
                    const input = document.getElementById('mobile-expense-receipt-inline');
                    if (input) input.value = '';
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleExpenseSubmit}
            disabled={expenseSubmitting || !expenseForm.purchaseDate || !expenseForm.category || !expenseForm.itemDescription || !expenseForm.amountDollars}
            sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem' }}
          >
            {expenseSubmitting ? <CircularProgress size={24} /> : 'SUBMIT EXPENSE'}
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );

  // NEW INCIDENT VIEW
  const renderNewIncidentView = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
          QUICK INCIDENT
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tap to create an incident and go on scene
        </Typography>

        {isIncidentRestricted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Incident creation is restricted until required core classes are completed and approved.
          </Alert>
        )}

        {incidentCreateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIncidentCreateError('')}>
            {incidentCreateError}
          </Alert>
        )}

        <Stack spacing={1.5}>
          {INCIDENT_TYPES.filter(shouldShowMobileIncidentType).map(type => (
            <Button
              key={type}
              fullWidth
              variant="contained"
              color={
                type.toLowerCase() === 'accident' ? 'error' :
                type.toLowerCase() === 'debris' ? 'warning' : 'success'
              }
              onClick={() => doQuickIncident(type)}
              disabled={submitting || myIncidents.length > 0 || isIncidentRestricted}
              sx={{ height: 56, fontSize: '1rem', fontWeight: 800 }}
            >
              {getTenCodeLabel(type, useTenCodes) || type.toUpperCase()}
            </Button>
          ))}
          
          <Divider sx={{ my: 1 }} />
          
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            onClick={() => doQuickIncident('Assist Motorist')}
            disabled={submitting || myIncidents.length > 0 || isIncidentRestricted}
            sx={{ height: 56, fontSize: '1rem', fontWeight: 800 }}
          >
            SERVICE CALL
          </Button>
        </Stack>

        {myIncidents.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Clear your active incident before creating a new one.
          </Alert>
        )}
      </Paper>
    </Stack>
  );

  // PROFILE VIEW
  const renderProfileView = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack alignItems="center" spacing={2}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
            {user?.rangerId?.charAt(0) || 'R'}
          </Avatar>
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={800}>
              {user?.rangerId || 'Ranger'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          SETTINGS
        </Typography>
        
        <List disablePadding>
          <ListItem sx={{ px: 0 }}>
            <ListItemAvatar>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemAvatar>
            <ListItemText 
              primary="Dark Mode" 
              secondary={mode === 'dark' ? 'On' : 'Off'}
            />
            <Switch 
              checked={mode === 'dark'} 
              onChange={toggleColorMode}
            />
          </ListItem>
        </List>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          VIEW PREFERENCES
        </Typography>

        <List disablePadding>
          <ListItem sx={{ px: 0 }}>
            <ListItemText
              primary="Force Desktop View"
              secondary="Use the desktop layout on larger tablets. Turn this off anytime to return to the mobile dashboard."
            />
            <Switch
              checked={forceDesktopView}
              onChange={(event) => setForceDesktopView(event.target.checked)}
            />
          </ListItem>
        </List>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          SESSION
        </Typography>
        
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{ fontWeight: 700 }}
        >
          LOG OUT
        </Button>
      </Paper>

      <Typography variant="caption" color="text.secondary" textAlign="center">
        Ranger Vault Mobile v1.0
      </Typography>
    </Stack>
  );

  // ============== MAIN RENDER ==============
  return (
    <Box sx={{ 
      minHeight: '100dvh', 
      display: 'flex', 
      flexDirection: 'column', 
      bgcolor: 'background.default' 
    }}>
      {/* Header */}
      <Paper 
        square 
        elevation={2}
        sx={{ 
          pt: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          pb: 1.5,
          px: 1.5,
          display: 'flex', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
          RANGER VAULT
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddCircleIcon />}
          onClick={() => setNewIncidentOpen(true)}
          disabled={myIncidents.length > 0 || isIncidentRestricted}
          sx={{ fontWeight: 700, px: 2 }}
        >
          NEW
        </Button>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, pb: 10 }}>
        {incidentCreateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIncidentCreateError('')}>
            {incidentCreateError}
          </Alert>
        )}

        {isIncidentRestricted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Incident creation is restricted for your account until required core classes are completed and approved.
          </Alert>
        )}

        {navValue === 0 && renderHomeView()}
        {navValue === 1 && renderDispatchView()}
        {navValue === 2 && renderExpenseView()}
        {navValue === 3 && renderProfileView()}
      </Box>

      {/* Bottom Navigation */}
      <Paper 
        square 
        elevation={8}
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1000,
          pb: 'env(safe-area-inset-bottom)'
        }}
      >
        <BottomNavigation
          value={navValue}
          onChange={(e, newValue) => setNavValue(newValue)}
          showLabels
        >
          <BottomNavigationAction label="Home" icon={<HomeIcon />} />
          <BottomNavigationAction 
            label="Dispatch" 
            icon={
              <Badge badgeContent={filteredWazeAlerts.length} color="error" max={99}>
                <NotificationsActiveIcon />
              </Badge>
            } 
          />
          <BottomNavigationAction 
            label="Expense" 
            icon={<ReceiptLongIcon />} 
          />
          <BottomNavigationAction label="Profile" icon={<PersonIcon />} />
        </BottomNavigation>
      </Paper>

      <Dialog open={emergencyConfirmOpen} onClose={closeEmergencyConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Emergency Activation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ textTransform: 'uppercase' }}>
              Emergency alert will activate in {emergencyCountdown} second{emergencyCountdown === 1 ? '' : 's'}.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Press cancel to stop activation before the countdown ends.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmergencyConfirm}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ===== EDIT INCIDENT DIALOG ===== */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullScreen
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            {editIncident?.incidentType?.replace(/-/g, ' ').toUpperCase() || 'INCIDENT'}
          </Typography>
          <IconButton onClick={() => setEditOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Stack spacing={2.5}>
            {/* Vehicle Info */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                VEHICLE INFO
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Year"
                    value={editForm.vehicle.year}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, year: e.target.value } })}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Make"
                    value={editForm.vehicle.make}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, make: e.target.value } })}
                    size="small"
                    sx={{ flex: 1.5 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Model"
                    value={editForm.vehicle.model}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, model: e.target.value } })}
                    size="small"
                    sx={{ flex: 1.5 }}
                  />
                  <TextField
                    label="Color"
                    value={editForm.vehicle.color}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, color: e.target.value } })}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="State"
                    value={editForm.vehicle.state}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, state: e.target.value.toUpperCase() } })}
                    size="small"
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 2 }}
                  />
                  <TextField
                    label="Plate"
                    value={editForm.vehicle.plate}
                    onChange={(e) => setEditForm({ ...editForm, vehicle: { ...editForm.vehicle, plate: e.target.value.toUpperCase() } })}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                </Stack>
              </Stack>
            </Box>

            {/* Disposition */}
            <Box>
              <Typography 
                variant="subtitle2" 
                fontWeight={700} 
                sx={{ mb: 1, color: editErrors.includes('disposition') ? 'error.main' : 'text.primary' }}
              >
                DISPOSITION {editErrors.includes('disposition') && '(REQUIRED)'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 0.75,
                p: editErrors.includes('disposition') ? 1 : 0,
                border: editErrors.includes('disposition') ? '2px solid' : 'none',
                borderColor: 'error.main',
                borderRadius: 1,
              }}>
                {SERVICE_OPTIONS.map(service => (
                  <Chip
                    key={service}
                    label={getTenCodeLabel(service, useTenCodes) || service}
                    onClick={() => toggleService(service)}
                    color={editForm.services.includes(service) ? 'primary' : 'default'}
                    variant={editForm.services.includes(service) ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Notes */}
            <Box>
              <Typography 
                variant="subtitle2" 
                fontWeight={700} 
                sx={{ mb: 1, color: editErrors.includes('notes') ? 'error.main' : 'text.primary' }}
              >
                NOTES {editErrors.includes('notes') && '(REQUIRED)'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Describe the incident..."
                value={editForm.notes}
                onChange={(e) => { 
                  setEditForm({ ...editForm, notes: e.target.value }); 
                  setEditErrors(prev => prev.filter(err => err !== 'notes')); 
                }}
                error={editErrors.includes('notes')}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
          {editErrors.length > 0 && (
            <Alert severity="error" sx={{ width: '100%', mb: 1 }}>
              Please fill in all required fields
            </Alert>
          )}
          {incompleteCount >= MAX_INCOMPLETE && (
            <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
              Max incomplete incidents reached. Complete on desktop.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={doFinishLater}
              disabled={submitting || incompleteCount >= MAX_INCOMPLETE}
              sx={{ py: 1.5 }}
            >
              {submitting ? <CircularProgress size={20} /> : 'FINISH LATER'}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={doSaveAndClear}
              disabled={submitting}
              sx={{ py: 1.5 }}
            >
              {submitting ? <CircularProgress size={20} /> : 'SAVE & CLEAR'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ===== INCOMPLETE WARNING DIALOG ===== */}
      <Dialog
        open={incompleteWarningOpen}
        onClose={() => setIncompleteWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'warning.contrastText' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon />
            <Typography variant="h6" fontWeight={800}>INCOMPLETE INCIDENTS</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            You have {incompleteCount} incomplete incidents. You must complete these on the desktop before using "Finish Later" again.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Failure to maintain accurate notes may result in removal of the "Finish Later" feature.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            fullWidth 
            variant="contained" 
            color="warning"
            onClick={() => setIncompleteWarningOpen(false)}
            sx={{ fontWeight: 700 }}
          >
            I UNDERSTAND
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== STATUS CHECK MODAL ===== */}
      <Dialog
        open={statusModalOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        keepMounted
        BackdropProps={{ sx: statusBackdropStyles }}
      >
        <DialogTitle sx={{ fontWeight: 800, textTransform: 'uppercase' }}>
          Status Check Required
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>
              YOU'VE BEEN ON SCENE FOR {statusModalIncident?.minutes ?? statusCheckSettings.defaultMinutes}+ MINUTES.
            </Alert>
            {statusModalIncident?.incident && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', mb: 1 }}>
                  {statusModalIncident.incident.incidentType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CAD {statusModalIncident.incident.cadId || 'PENDING'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Started {statusModalIncident.incident.occurredAtUtc
                    ? formatUtcToLocal(statusModalIncident.incident.occurredAtUtc)
                    : 'Unknown'}
                </Typography>
              </Paper>
            )}
            <Typography variant="body2" color="text.secondary">
              Tap "10-4" to reset the timer or "Clear" to finish the call details now.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleStatusCheckClear}
            sx={{ textTransform: 'uppercase', fontWeight: 700 }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleStatusCheckAcknowledge}
            sx={{ textTransform: 'uppercase', fontWeight: 700 }}
          >
            10-4
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== NEW INCIDENT MODAL ===== */}
      <Dialog
        open={newIncidentOpen}
        onClose={() => setNewIncidentOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { m: 2, maxHeight: 'calc(100% - 32px)' } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            NEW INCIDENT
          </Typography>
          <IconButton onClick={() => setNewIncidentOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select incident type to go on scene
          </Typography>

          {isIncidentRestricted && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Incident creation is restricted until required core classes are completed and approved.
            </Alert>
          )}

          {incidentCreateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIncidentCreateError('')}>
              {incidentCreateError}
            </Alert>
          )}

          <Stack spacing={1.5}>
            {INCIDENT_TYPES.filter(shouldShowMobileIncidentType).map(type => (
              <Button
                key={type}
                fullWidth
                variant="contained"
                color={
                  type.toLowerCase() === 'accident' ? 'error' :
                  type.toLowerCase() === 'debris' ? 'warning' : 'success'
                }
                onClick={() => {
                  doQuickIncident(type);
                  setNewIncidentOpen(false);
                }}
                disabled={submitting || isIncidentRestricted}
                sx={{ height: 52, fontSize: '0.95rem', fontWeight: 800 }}
              >
                {getTenCodeLabel(type, useTenCodes) || type.toUpperCase()}
              </Button>
            ))}
            
            <Divider sx={{ my: 0.5 }} />
            
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={() => {
                doQuickIncident('Assist Motorist');
                setNewIncidentOpen(false);
              }}
              disabled={submitting || isIncidentRestricted}
              sx={{ height: 52, fontSize: '0.95rem', fontWeight: 800 }}
            >
              SERVICE CALL
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ===== EXPENSE DIALOG ===== */}
      <Dialog
        open={expenseDialogOpen}
        onClose={() => setExpenseDialogOpen(false)}
        fullScreen
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            LOG EXPENSE
          </Typography>
          <IconButton onClick={() => setExpenseDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 10 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              For transparency only. No reimbursement provided.
            </Typography>
          </Alert>

          {expenseSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Expense logged successfully!
            </Alert>
          )}

          {expenseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {expenseError}
            </Alert>
          )}

          <Stack spacing={2.5}>
            {/* Date */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Date *"
                type="date"
                value={expenseForm.purchaseDate}
                onChange={handleExpenseChange('purchaseDate')}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
                size="small"
              />
              <Button
                variant="outlined"
                onClick={() => setExpenseForm(prev => ({
                  ...prev,
                  purchaseDate: new Date().toISOString().split('T')[0]
                }))}
                sx={{ minWidth: 80, fontWeight: 700 }}
              >
                TODAY
              </Button>
            </Box>

            {/* Category */}
            <FormControl fullWidth required size="small">
              <InputLabel>Category *</InputLabel>
              <Select
                value={expenseForm.category}
                label="Category *"
                onChange={handleExpenseChange('category')}
              >
                {EXPENSE_CATEGORIES.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {cat.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Item Description */}
            <TextField
              label="Item(s) Purchased *"
              value={expenseForm.itemDescription}
              onChange={handleExpenseChange('itemDescription')}
              required
              fullWidth
              size="small"
              placeholder="e.g., 2x LED flares, jump cables"
              inputProps={{ maxLength: 512 }}
            />

            {/* Amount */}
            <TextField
              label="Amount ($) *"
              type="number"
              value={expenseForm.amountDollars}
              onChange={handleExpenseChange('amountDollars')}
              required
              fullWidth
              size="small"
              inputProps={{ min: 0.01, step: 0.01 }}
              placeholder="0.00"
            />

            {/* Notes */}
            <TextField
              label={EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category))?.requiresNotes
                ? "Notes * (required)"
                : "Notes (optional)"
              }
              value={expenseForm.notes}
              onChange={handleExpenseChange('notes')}
              required={EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category))?.requiresNotes}
              fullWidth
              size="small"
              multiline
              rows={2}
              inputProps={{ maxLength: 1024 }}
            />

            {/* Receipt Upload */}
            <Box>
              <input
                id="mobile-expense-receipt"
                type="file"
                accept="image/*,.pdf"
                onChange={handleExpenseReceiptChange}
                style={{ display: 'none' }}
                capture="environment"
              />
              <label htmlFor="mobile-expense-receipt">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  sx={{ fontWeight: 600 }}
                >
                  {expenseReceipt ? 'CHANGE RECEIPT' : 'UPLOAD RECEIPT (OPTIONAL)'}
                </Button>
              </label>
              {expenseReceipt && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                    {expenseReceipt.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setExpenseReceipt(null);
                      const input = document.getElementById('mobile-expense-receipt');
                      if (input) input.value = '';
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          p: 2, 
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleExpenseSubmit}
            disabled={expenseSubmitting || expenseSuccess}
            sx={{ height: 48, fontWeight: 800 }}
          >
            {expenseSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : expenseSuccess ? (
              'LOGGED!'
            ) : (
              'LOG EXPENSE'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Self-Cancel Dialog */}
      <Dialog open={selfCancelOpen} onClose={() => !selfCancelLoading && setSelfCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Your Emergency</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Reason for cancellation"
              value={selfCancelReason}
              onChange={(e) => setSelfCancelReason(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="Activated by mistake">Activated by mistake</MenuItem>
              <MenuItem value="Situation resolved">Situation resolved</MenuItem>
              <MenuItem value="False alarm">False alarm</MenuItem>
              <MenuItem value="Testing">Testing</MenuItem>
            </TextField>
            <TextField
              label="Enter your PIN"
              value={selfCancelPin}
              onChange={(e) => setSelfCancelPin(e.target.value)}
              fullWidth
              required
              autoComplete="off"
            />
            {selfCancelError && (
              <Alert severity="error">{selfCancelError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelfCancelOpen(false)} disabled={selfCancelLoading}>
            Go Back
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSelfCancelEmergency}
            disabled={selfCancelLoading}
          >
            {selfCancelLoading ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
